/**
 * Timesheets advanced-filter document — a versioned value the web filter panel
 * (`TimesheetFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * timesheet index. One schema + ONE evaluator, both in `@releve/shared` (the
 * places/Impact/AR/AP/bank-feed/Vault template), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyTimesheetFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session
 * (saved/shared presets were deliberately removed from the places build; same
 * posture here). There is NO seeded default: the list opens on every timesheet
 * the caller may see, so `EMPTY_TIMESHEET_FILTER_DOCUMENT` is the only
 * starting value (the bank feed's `DEFAULT_*` quirk is bank-feed-only).
 *
 * ── v1 vocabulary ───────────────────────────────────────────────────────────
 * The legacy `EntitiesDataGrid` carried two toolbar filters (project and
 * business). v1 keeps both, and adds the two fields the slim index made
 * cheap and the grid now shows as columns: `person_name` and `visibility`.
 * Adding a filter for a column that exists is the port; nothing else is
 * net-new (no hours ranges — the rollups are windowed already).
 *
 * `last_entry_date` is the one date family: a plain `date` column on the wire,
 * so it needs no instant→day reduction (see `dateValue` below).
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both: two filter states means the panel silently shrinks the
 * header option lists, each has its own Clear, and the badge counts only one
 * of them. The timesheets grid ends with ZERO filterable columns.
 *
 * v1 fields are `timesheet.<field>` over the MAPPED grid rows, not the wire
 * row — evaluation runs AFTER the mapper and BEFORE the grid shell
 * (`hasUpstreamFilters`). The field string carries its namespace, so a later
 * vocabulary stays additive.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
/**
 * The three resolved NAME fields plus the visibility tier. Names, not ids:
 * the index projects `project_name` / `business_name` / `person_name` and the
 * columns are bound to them, so filtering on the id would offer the user a
 * list that does not match what the cells say.
 *
 * `timesheet.visibility` is a CLOSED set on the wire ('members' | 'admins' |
 * 'private') but stays a plain string list here — `values` is the panel's
 * selection, and the row accessor is typed `string` so a narrower mapper union
 * satisfies it structurally.
 */
export const TIMESHEET_FILTER_STRING_FIELDS = [
    "timesheet.project_name",
    "timesheet.business_name",
    "timesheet.person_name",
    "timesheet.visibility",
];
/** The one date family — the timesheet's most recent time entry. */
export const TIMESHEET_FILTER_DATE_FIELDS = ["timesheet.last_entry_date"];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * TWO families, so a two-armed union. The arms have DISJOINT op sets
 * ('in'/'is_empty' vs the three date ops), which is what lets
 * `matchesCondition` narrow on `op` alone.
 *
 * EMPTY-SET SEMANTICS (from the shared primitives, not re-decided here): an
 * `in` condition with zero values is REJECTED by the schema. "Nothing
 * checked" is spelled as NO CONDITION AT ALL — the panel drops the family —
 * and never as a condition that would match nothing.
 */
const stringConditionSchema = makeStringConditionSchema(TIMESHEET_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(TIMESHEET_FILTER_DATE_FIELDS);
export const timesheetFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const timesheetFilterDocumentSchema = makeFilterDocumentSchema(timesheetFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_TIMESHEET_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary with NO default case (the reason `sustainabilityFilters` keeps
 * them: a new vocabulary entry is a `TS2366: Function lacks ending return
 * statement` until it is handled here, instead of compiling clean and
 * filtering the wrong column).
 */
function stringValue(row, field) {
    switch (field) {
        case "timesheet.project_name":
            return row.project_name ?? "";
        case "timesheet.business_name":
            return row.business_name ?? "";
        case "timesheet.person_name":
            return row.person_name ?? "";
        case "timesheet.visibility":
            // Never null (CHECK-constrained, NOT NULL) — no `?? ''` fallback.
            return row.visibility;
    }
}
/**
 * The calendar day (`yyyy-MM-dd`) to compare. `last_entry_date` is a plain
 * `date` column — `MAX(time_entries.entry_date)` — so it is already the day
 * the cell renders and goes through VERBATIM: no `toLocalCalendarDay`, no
 * `new Date(...)` round-trip (parsing a date-only string as an instant is
 * exactly the UTC shift the calendar-day rule exists to prevent). Compared as
 * STRINGS — lexicographic order is date order for this shape.
 */
function dateValue(row, field) {
    switch (field) {
        case "timesheet.last_entry_date":
            return row.last_entry_date;
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped timesheet rows (AND of all conditions —
 * v1 `match` is 'all' only). An empty document returns the input array
 * unchanged (identity — callers can rely on reference equality to skip
 * re-renders).
 */
export function applyTimesheetFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveTimesheetConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=timesheetFilters.js.map